import { useEffect, useRef, useState } from "react";
import api, { fmtNZD } from "@/lib/api";
import { errMsg } from "@/lib/hsUtil";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import LabourFactorsConfig from "@/pages/project/LabourFactorsConfig";
import Directory from "@/pages/configuration/Directory";
import LabourRatesConfig from "@/pages/configuration/LabourRatesConfig";
import WorkerCostCalc from "@/pages/configuration/WorkerCostCalc";
import WorkerProfileModal from "@/pages/configuration/WorkerProfileModal";
import TaxAndLevies from "@/pages/configuration/TaxAndLevies";
import HolidaysConfig from "@/pages/configuration/HolidaysConfig";
import SupplierNamesConfig from "@/pages/configuration/SupplierNamesConfig";
import RemindersConfig from "@/pages/configuration/RemindersConfig";
import WorkerLeaveSettings from "@/pages/configuration/WorkerLeaveSettings";
import AddStaffDialog from "@/pages/configuration/AddStaffDialog";
import ReauthModal from "@/components/ReauthModal";
import SetPasswordModal from "@/components/SetPasswordModal";
import NumberInput from "@/components/NumberInput";
import SaveIndicator from "@/components/SaveIndicator";
import useAutosave from "@/hooks/useAutosave";

// Module-level so inputs keep focus (an inner component remounts on every keystroke).
function F({ label, hint, required, children }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs text-silver mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] opacity-60 mt-0.5">{hint}</span>}
    </label>
  );
}

function CompletionTemplate({ s, setS }) {
  const [def, setDef] = useState("");
  useEffect(() => { api.get("/report/completion-template").then(r => setDef(r.data.default)).catch(() => {}); }, []);
  return (
    <div className="md:col-span-2">
      <F label="Report Builder — Project completion template" hint="Placeholders: {project}, {client}, {completion date}, {date range}. Saved with the Save settings button.">
        <textarea className="input w-full h-56" value={s.completion_template || def} onChange={e => setS({ ...s, completion_template: e.target.value })} data-testid="settings-completion-template" />
        <button type="button" className="btn-ghost text-xs mt-1" onClick={() => setS({ ...s, completion_template: def })} data-testid="settings-completion-reset">Reset to default text</button>
      </F>
    </div>
  );
}


function LogoUpload() {
  const [url, setUrl] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/settings/logo", { responseType: "blob" }).then(r => setUrl(URL.createObjectURL(r.data))).catch(() => setUrl(""));
  useEffect(() => { load(); }, []);
  async function pick(e) {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/image\/(png|jpe?g)/.test(f.type)) { setMsg("PNG or JPG only"); return; }
    if (f.size > 5 * 1024 * 1024) { setMsg("Max 5MB"); return; }
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMsg("Logo saved ✓"); load(); }
    catch (ex) { setMsg(ex?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  }
  return (
    <F label="Company logo" hint="PNG or JPG, max 5MB. Used on Report Builder previews and PDFs.">
      <div className="flex items-center gap-3 flex-wrap" data-testid="settings-logo">
        {url ? <img src={url} alt="Company logo" className="h-12 bg-white p-1 rounded" data-testid="settings-logo-img" /> : <span className="text-xs opacity-60">No logo yet</span>}
        <input type="file" accept="image/png,image/jpeg" onChange={pick} data-testid="settings-logo-input" />
        {msg && <span className="text-xs" data-testid="settings-logo-msg">{msg}</span>}
      </div>
    </F>
  );
}


export default function Configuration() {
  const [tab, setTab] = useState("people");
  return (
    <div className="space-y-3" data-testid="config-page">
      <h1 className="text-2xl">Configuration</h1>
      <div className="flex gap-2 flex-wrap">
        {[["people","People & Pay"], ["worker-cost","Worker cost calculator"], ["tax-levies","Tax and levies"], ["directory","Directory"], ["labour-rates","Labour rates"], ["products","Products"], ["labour-factors","Labour factors"], ["task-templates","Task templates"], ["announcements","Announcements"], ["holidays","Public holidays"], ["reminders","Timesheet reminders"], ["supplier-names","Supplier names on reports"], ["settings","Settings"], ["import","Import from existing Hub"]].map(([k,l]) => (
          <button key={k} data-testid={`cfg-${k}`} onClick={()=>setTab(k)}
            className={`px-3 py-1.5 text-sm rounded ${tab===k?"bg-silver text-black":"text-white border border-neutral-800"}`}>{l}</button>
        ))}
      </div>
      {tab === "people" && <People />}
      {tab === "worker-cost" && <WorkerCostCalc />}
      {tab === "tax-levies" && <TaxAndLevies />}
      {tab === "directory" && <Directory />}
      {tab === "labour-rates" && <LabourRatesConfig />}
      {tab === "products" && <Products />}
      {tab === "labour-factors" && <LabourFactorsConfig />}
      {tab === "task-templates" && <TaskTemplates />}
      {tab === "announcements" && <Announcements />}
      {tab === "holidays" && <HolidaysConfig />}
      {tab === "reminders" && <RemindersConfig />}
      {tab === "supplier-names" && <SupplierNamesConfig />}
      {tab === "settings" && <Settings />}
      {tab === "import" && <ImportTool />}
    </div>
  );
}

function Subcontractors() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", trade: "", phone: "" });
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  async function load(){ const {data} = await api.get("/suppliers"); setList((data||[]).filter(s=>!!(s.trade||"").trim())); }
  useEffect(() => { load(); }, []);
  async function add(e){
    e.preventDefault();
    if (!form.name || !form.trade) { alert("Name and trade are required for a subcontractor"); return; }
    await api.post("/suppliers", form);
    setForm({name:"",email:"",trade:"",phone:""}); load();
    setMsg("Subcontractor added ✓"); setTimeout(()=>setMsg(""), 2000);
  }
  async function saveEdit(){ await api.patch(`/suppliers/${editing.id}`, editing); setEditing(null); load(); setMsg("Saved ✓"); setTimeout(()=>setMsg(""), 2000); }
  async function del(s){ if (!window.confirm(`Delete subcontractor "${s.name}"? Are you sure?`)) return; await api.delete(`/suppliers/${s.id}`); load(); setMsg("Deleted ✓"); setTimeout(()=>setMsg(""), 2000); }
  return (
    <div className="card" data-testid="subcontractors-tab">
      {msg && <div className="text-green-400 text-sm mb-2">{msg}</div>}
      <p className="text-sm opacity-70 mb-2">Subcontractors are suppliers with a trade (plumber, electrician, roofer, …).</p>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <input className="input" placeholder="Company name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="sub-name"/>
        <input className="input" placeholder="Email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="sub-email"/>
        <input className="input" placeholder="Phone" value={form.phone||""} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="sub-phone"/>
        <input className="input" placeholder="Trade (e.g. Plumber)" value={form.trade} onChange={e=>setForm({...form,trade:e.target.value})} data-testid="sub-trade"/>
        <button className="btn-silver" data-testid="btn-add-subcontractor">+ Add subcontractor</button>
      </form>
      <table className="table"><thead><tr><th>Trade</th><th>Company</th><th>Email</th><th>Phone</th><th></th></tr></thead>
        <tbody>{list.map(s=> editing?.id === s.id ? (
          <tr key={s.id} data-testid={`sub-edit-${s.id}`}>
            <td><input className="input" value={editing.trade||""} onChange={e=>setEditing({...editing, trade: e.target.value})}/></td>
            <td><input className="input" value={editing.name||""} onChange={e=>setEditing({...editing, name: e.target.value})}/></td>
            <td><input className="input" value={editing.email||""} onChange={e=>setEditing({...editing, email: e.target.value})}/></td>
            <td><input className="input" value={editing.phone||""} onChange={e=>setEditing({...editing, phone: e.target.value})}/></td>
            <td className="flex gap-2 text-xs"><button className="hub-link" onClick={saveEdit} data-testid={`btn-sub-save-${s.id}`}>Save</button><button className="opacity-70" onClick={()=>setEditing(null)}>Cancel</button></td>
          </tr>
        ) : (
          <tr key={s.id} data-testid={`sub-row-${s.id}`}><td>{s.trade}</td><td>{s.name}</td><td>{s.email||"—"}</td><td>{s.phone||"—"}</td>
            <td className="flex gap-2 text-xs">
              <button className="hub-link" onClick={()=>setEditing({...s})} data-testid={`btn-sub-edit-${s.id}`}>Edit</button>
              <button className="text-red-400" onClick={()=>del(s)} data-testid={`btn-sub-del-${s.id}`}>Delete</button>
            </td>
          </tr>
        ))}
        {list.length===0 && <tr><td colSpan="5" className="opacity-50">No subcontractors yet. Add a supplier with a Trade field above.</td></tr>}
        </tbody></table>
    </div>
  );
}

function TaskTemplates() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", tasksText: "" });
  async function load(){ const {data} = await api.get("/task-template-sets"); setList(data||[]); }
  useEffect(() => { load(); }, []);
  async function add(e){
    e.preventDefault();
    if (!form.name) return;
    const tasks = form.tasksText.split("\n").map(s=>s.trim()).filter(Boolean).map(name => ({ name }));
    await api.post("/task-template-sets", { name: form.name, tasks });
    setForm({ name:"", tasksText:"" }); load();
  }
  async function del(id){
    if (!window.confirm("Delete this template set?")) return;
    await api.delete(`/task-template-sets/${id}`); load();
  }
  return (
    <div className="card" data-testid="task-templates-tab">
      <p className="text-sm opacity-70 mb-2">Reusable task sets applied from Project → Tasks.</p>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <input className="input" placeholder="Template set name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="tpl-name"/>
        <textarea className="input" rows={4} placeholder="One task per line" value={form.tasksText} onChange={e=>setForm({...form,tasksText:e.target.value})} data-testid="tpl-tasks"/>
        <button className="btn-silver md:col-span-2" data-testid="btn-add-template-set">+ Add template set</button>
      </form>
      <table className="table"><thead><tr><th>Name</th><th>Tasks</th><th></th></tr></thead>
        <tbody>{list.map(s=>(
          <tr key={s.id} data-testid={`tpl-row-${s.id}`}>
            <td>{s.name}</td>
            <td className="text-xs opacity-80">{(s.tasks||[]).map(t=>t.name).join(", ") || "—"}</td>
            <td><button className="text-xs text-red-400" onClick={()=>del(s.id)}>Delete</button></td>
          </tr>
        ))}
        {list.length===0 && <tr><td colSpan="3" className="opacity-50">No template sets yet.</td></tr>}
        </tbody></table>
    </div>
  );
}

function Announcements() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", urgent: false });
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  async function load(){ const {data} = await api.get("/announcements"); setList(data||[]); }
  useEffect(() => { load(); }, []);
  async function add(e){
    e.preventDefault();
    if (!form.title || !form.body) return;
    await api.post("/announcements", form);
    setForm({ title:"", body:"", urgent:false }); load();
    setMsg("Announcement posted ✓"); setTimeout(()=>setMsg(""), 2000);
  }
  async function saveEdit(){ await api.patch(`/announcements/${editing.id}`, editing); setEditing(null); load(); setMsg("Saved ✓"); setTimeout(()=>setMsg(""), 2000); }
  async function del(id){ if (!window.confirm("Delete this announcement? Are you sure?")) return; await api.delete(`/announcements/${id}`); load(); setMsg("Deleted ✓"); setTimeout(()=>setMsg(""), 2000); }
  return (
    <div className="card" data-testid="announcements-tab">
      {msg && <div className="text-green-400 text-sm mb-2">{msg}</div>}
      <p className="text-sm opacity-70 mb-2">Posted to the team. Tick 'Urgent' to flag with a red badge.</p>
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <input className="input" placeholder="Title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="ann-title"/>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.urgent} onChange={e=>setForm({...form,urgent:e.target.checked})} data-testid="ann-urgent"/>Urgent</label>
        <textarea className="input md:col-span-2" rows={3} placeholder="Message" value={form.body} onChange={e=>setForm({...form,body:e.target.value})} data-testid="ann-body"/>
        <button className="btn-silver md:col-span-2" data-testid="btn-add-announcement">+ Post announcement</button>
      </form>
      <table className="table"><thead><tr><th>Title</th><th>Message</th><th>Urgent</th><th>Posted</th><th></th></tr></thead>
        <tbody>{list.map(a=> editing?.id === a.id ? (
          <tr key={a.id}>
            <td><input className="input" value={editing.title} onChange={e=>setEditing({...editing, title: e.target.value})}/></td>
            <td><input className="input" value={editing.body} onChange={e=>setEditing({...editing, body: e.target.value})}/></td>
            <td><input type="checkbox" checked={!!editing.urgent} onChange={e=>setEditing({...editing, urgent: e.target.checked})}/></td>
            <td className="text-xs opacity-70">{(a.created_at||"").slice(0,16).replace("T"," ")}</td>
            <td className="flex gap-2 text-xs"><button className="hub-link" onClick={saveEdit} data-testid={`btn-ann-save-${a.id}`}>Save</button><button className="opacity-70" onClick={()=>setEditing(null)}>Cancel</button></td>
          </tr>
        ) : (
          <tr key={a.id} data-testid={`ann-row-${a.id}`}>
            <td>{a.title}</td>
            <td className="text-xs opacity-80">{a.body}</td>
            <td>{a.urgent ? <span className="badge badge-red">Urgent</span> : "—"}</td>
            <td className="text-xs opacity-70">{(a.created_at||"").slice(0,16).replace("T"," ")}</td>
            <td className="flex gap-2 text-xs">
              <button className="hub-link" onClick={()=>setEditing({...a})} data-testid={`btn-ann-edit-${a.id}`}>Edit</button>
              <button className="text-red-400" onClick={()=>del(a.id)}>Delete</button>
            </td>
          </tr>
        ))}
        {list.length===0 && <tr><td colSpan="5" className="opacity-50">No announcements yet.</td></tr>}
        </tbody></table>
    </div>
  );
}

function People() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [reset, setReset] = useState(null);
  const [reauth, setReauth] = useState(null);
  const [editing, setEditing] = useState(null);
  const [leaveFor, setLeaveFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const loadSeq = useRef(0);
  async function load() {
    const seq = ++loadSeq.current;
    const { data } = await api.get(`/workers?include_archived=${showArchived}`);
    if (seq === loadSeq.current) setList(data);
  }
  useEffect(() => { load(); }, [showArchived]);
  async function logoutAllFor(uid, adminPw) {
    await api.post(`/auth/users/${uid}/logout-all`, { password: adminPw });
    alert("All sessions revoked for that user."); setReauth(null);
  }
  const autosave = useAutosave(async (wid, patch) => {
    await api.patch(`/workers/${wid}`, patch);
  });
  function setRate(wid, pay_rate) {
    // Update local list immediately so the box reflects what Luke typed (fixes
    // "only 1 entry" — the old defaultValue render was blank on reload).
    setList(prev => prev.map(w => w.id === wid ? { ...w, pay_rate } : w));
    autosave.schedule(wid, { pay_rate: parseFloat(pay_rate || 0) });
  }
  async function archive(w) {
    if (!window.confirm(`Archive ${w.name} (${w.email || "no email"})?\n\nTheir timesheets and pay history stay intact. They won't be able to log in or appear in pickers until restored.\n\nClick OK to archive, Cancel to keep.`)) return;
    try {
      await api.post(`/workers/${w.id}/archive`);
      load();
    } catch (e) {
      alert(errMsg(e, "Archive failed"));
    }
  }
  async function unarchive(w) {
    try { await api.post(`/workers/${w.id}/unarchive`); load(); }
    catch (e) { alert(errMsg(e, "Restore failed")); }
  }
  async function deleteForever(w) {
    const typed = window.prompt(`Permanently delete ${w.name} (${w.email || "no email"})?\n\nThis can't be undone. Only allowed when they have no timesheets, bills, pay runs or incidents.\n\nType DELETE to confirm.`);
    if (typed === null) return;
    if (typed !== "DELETE") { alert("Not deleted: you must type DELETE."); return; }
    try { await api.delete(`/workers/${w.id}/permanent`); load(); }
    catch (e) { alert(errMsg(e, "Delete failed")); }
  }

  return (
    <div className="space-y-3">
      {reauth && (
        <ReauthModal title="Confirm — Log this user out of all devices"
                     onCancel={()=>setReauth(null)} submitLabel="Confirm"
                     onConfirmed={(pw) => logoutAllFor(reauth.uid, pw)}/>
      )}
      {reset && <SetPasswordModal target={reset} onClose={(done) => { setReset(null); if (done) load(); }} />}
      {editing && (
        <WorkerProfileModal worker={editing} onClose={()=>setEditing(null)}
                            onSaved={()=>{ setEditing(null); load(); }}/>
      )}

      <div className="card flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs opacity-70">
          Workers who have an email address can log in. Pay rates and all personal fields are Admin-only — Workers never see any of this.
        </div>
        <div className="flex gap-2 items-center">
          <SaveIndicator status={autosave.status} data-testid="people-save-indicator"/>
          <label className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={showArchived} onChange={e=>setShowArchived(e.target.checked)} data-testid="people-archived-toggle"/>
            Show archived
          </label>
          {user?.role === "Admin" && <button className="btn-silver text-sm" onClick={() => setAdding(true)} data-testid="btn-add-staff">+ Add staff</button>}
          <Link to="/operations/payroll" className="btn-silver text-sm" data-testid="people-pay-run">Pay run →</Link>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Pay rate</th><th>Cost / Charge-out</th><th>Profile</th><th></th></tr></thead>
          <tbody>{list.map(w => (
            <tr key={w.id} data-testid={`worker-row-${w.id}`} className={w.archived ? "opacity-50" : ""}>
              <td>
                {w.name}
                {w.archived && <span className="ml-2 badge badge-grey" data-testid={`worker-archived-${w.id}`}>Archived</span>}
                {w.duplicate_user_email && (
                  <span className="ml-2 text-[10px] text-amber-300 border border-amber-300 px-1 rounded"
                        title={w.has_sessions ? "This row's user has active logins" : "This row's user has no logins yet"}
                        data-testid={`worker-dup-${w.id}`}>
                    duplicate email
                  </span>
                )}
              </td>
              <td>{w.email}</td>
              <td>{w.role}</td>
              <td><NumberInput className="input w-24" money value={w.pay_rate ?? ""} onChange={e=>setRate(w.id, e.target.value)} onBlur={e=>autosave.flushKey(w.id)} data-testid={`worker-rate-${w.id}`}/></td>
              <td className="text-xs">
                <div>Cost: {fmtNZD(w.cost_rate || 0)}</div>
                <div className="opacity-70">Charge-out: {fmtNZD(w.charge_out_rate || 0)} <span className="opacity-50">({w.charge_out_margin_pct || 15}%)</span></div>
                {w.cost_rate_source === "manual" && <span className="text-[10px] bg-amber-400 text-black px-1 rounded ml-1">manual</span>}
              </td>
              <td>
                {w.onboarding_complete
                  ? <span className="text-green-400" title="Profile complete" data-testid={`worker-dot-${w.id}-ok`}>●</span>
                  : <button className="hub-link text-xs" title={`Missing: ${(w.missing_fields||[]).join(", ")}`}
                            onClick={()=>setEditing(w)} data-testid={`worker-complete-profile-${w.id}`}>
                      <span className="text-amber-300">●</span> Complete profile
                    </button>}
              </td>
              <td className="flex flex-wrap gap-1 text-xs">
                <button className="btn-ghost" onClick={()=>setEditing(w)} data-testid={`btn-edit-${w.id}`}>Edit</button>
                <button className="btn-ghost" onClick={()=>setLeaveFor(w)} data-testid={`btn-leave-settings-${w.id}`}>Leave</button>
                {w.user_id && <button className="btn-ghost" onClick={()=>setReset({uid:w.user_id,wid:"",name:w.name})} data-testid={`btn-reset-${w.id}`}>Reset password</button>}
                {!w.user_id && w.email && !w.archived && <button className="btn-ghost text-silver" onClick={()=>setReset({uid:"",wid:w.id,name:w.name})} data-testid={`btn-create-login-${w.id}`}>Create login / Set temp password</button>}
                {w.user_id && <button className="btn-ghost text-amber-300" onClick={()=>setReauth({action:"logout", uid:w.user_id})} data-testid={`btn-logout-all-${w.id}`}>Log out all devices</button>}
                {!w.archived && w.user_id !== user?.id && <button className="btn-ghost text-red-400" onClick={()=>archive(w)} data-testid={`btn-archive-${w.id}`}>Archive</button>}
              {!w.archived && w.user_id === user?.id && <span className="text-[10px] opacity-60" title="You can't archive your own account">—</span>}
                {w.archived && <button className="btn-ghost text-green-400" onClick={()=>unarchive(w)} data-testid={`btn-unarchive-${w.id}`}>Restore</button>}
                {w.archived && user?.role === "Admin" && <button className="btn-ghost text-red-500" onClick={()=>deleteForever(w)} data-testid={`btn-delete-permanent-${w.id}`}>Delete permanently</button>}
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan="7" className="opacity-50">No workers to show.</td></tr>}
          </tbody>
        </table>
      </div>
      {leaveFor && <WorkerLeaveSettings worker={leaveFor} onClose={() => setLeaveFor(null)} />}
      {adding && <AddStaffDialog onClose={() => setAdding(false)} onAdded={load} />}
    </div>
  );
}

function Suppliers() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", trade: "" });
  async function load(){ const {data} = await api.get("/suppliers"); setList(data); }
  useEffect(() => { load(); }, []);
  async function add(e){ e.preventDefault(); await api.post("/suppliers", form); setForm({name:"",email:"",trade:""}); load(); }
  return (
    <div className="card">
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <input className="input" placeholder="Supplier name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
        <input className="input" placeholder="Email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
        <input className="input" placeholder="Trade" value={form.trade} onChange={e=>setForm({...form,trade:e.target.value})}/>
        <button className="btn-silver">+ Add</button>
      </form>
      <table className="table"><thead><tr><th>Name</th><th>Email</th><th>Trade</th></tr></thead>
        <tbody>{list.map(s=>(<tr key={s.id}><td>{s.name}</td><td>{s.email||"—"}</td><td>{s.trade||"—"}</td></tr>))}</tbody></table>
    </div>
  );
}

function Products() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", unit: "ea", rate: 0 });
  async function load(){ const {data} = await api.get("/products"); setList(data); }
  useEffect(() => { load(); }, []);
  async function add(e){ e.preventDefault(); await api.post("/products", form); setForm({name:"",unit:"ea",rate:0}); load(); }
  return (
    <div className="card">
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <input className="input" placeholder="Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
        <input className="input" placeholder="Unit" value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})}/>
        <NumberInput className="input" placeholder="Rate" value={form.rate} onChange={e=>setForm({...form,rate:parseFloat(e.target.value||0)})}/>
        <button className="btn-silver">+ Add</button>
      </form>
      <table className="table"><thead><tr><th>Name</th><th>Unit</th><th>Rate</th></tr></thead>
        <tbody>{list.map(p=>(<tr key={p.id}><td>{p.name}</td><td>{p.unit}</td><td>{fmtNZD(p.rate)}</td></tr>))}</tbody></table>
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  async function save() {
    await api.patch("/settings", s); setMsg("Settings saved ✓");
    setTimeout(() => setMsg(""), 2500);
  }
  if (!s) return null;
  return (
    <div className="card space-y-3" data-testid="settings-tab">
      {msg && <div className="text-green-400 text-sm" data-testid="settings-saved-msg">{msg}</div>}
      <h3 className="text-silver mb-1">Company</h3>
      <label className="flex items-start gap-2 border border-neutral-800 rounded p-3" data-testid="settings-leave-visible-label">
        <input type="checkbox" className="mt-1" checked={!!s.leave_visible_to_staff} onChange={e=>setS({...s,leave_visible_to_staff:e.target.checked})} data-testid="settings-leave-visible"/>
        <span><span className="font-medium">Show leave to staff</span>
          <span className="block text-xs opacity-60">Off: staff see "Under construction" on the Leave tab and can't request leave. On: staff can request leave. Admin and Managers always see leave. Press Save settings.</span></span>
      </label>
      <label className="flex items-start gap-2 border border-neutral-800 rounded p-3" data-testid="settings-payslips-visible-label">
        <input type="checkbox" className="mt-1" checked={!!s.payslips_visible_to_staff} onChange={e=>setS({...s,payslips_visible_to_staff:e.target.checked})} data-testid="settings-payslips-visible"/>
        <span><span className="font-medium">Show payslips to staff</span>
          <span className="block text-xs opacity-60">Off: staff have no Payslips tab and can't fetch payslips. On: staff see their own payslips. Separate from the leave switch. Press Save settings.</span></span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Company name"><input className="input" placeholder="Company name" value={s.name||""} onChange={e=>setS({...s,name:e.target.value})} data-testid="settings-company-name"/></F>
        <F label="Display name" hint="Shown in the app header"><input className="input" placeholder="Display name" value={s.display_name||""} onChange={e=>setS({...s,display_name:e.target.value})}/></F>
        <F label="Phone"><input className="input" placeholder="Phone" value={s.phone||""} onChange={e=>setS({...s,phone:e.target.value})} data-testid="settings-company-phone"/></F>
        <F label="Email"><input className="input" placeholder="name@example.com" value={s.email||""} onChange={e=>setS({...s,email:e.target.value})} data-testid="settings-company-email"/></F>
        <F label={<span>Labour rate source <span className="text-[10px] opacity-70">(where the two numbers above come from)</span></span>}>
          <select className="input" value={s.labour_cost_rate_source || "manual"}
                  onChange={e=>setS({...s,labour_cost_rate_source:e.target.value})} data-testid="lr-source">
            <option value="manual">Manual (type above)</option>
            <option value="template">From a worker-cost template</option>
            <option value="worker">From a specific worker</option>
          </select>
        </F>
        {(s.labour_cost_rate_source === "template") && (
          <F label="Template"><input className="input" placeholder="default_worker_calc" value={s.labour_cost_rate_source_id||""} onChange={e=>setS({...s,labour_cost_rate_source_id:e.target.value})} data-testid="lr-tpl-id"/></F>
        )}
        {(s.labour_cost_rate_source === "worker") && (
          <F label="Worker ID"><input className="input" placeholder="worker uuid" value={s.labour_cost_rate_source_id||""} onChange={e=>setS({...s,labour_cost_rate_source_id:e.target.value})} data-testid="lr-worker-id"/></F>
        )}
        <F label="Hours per day"><NumberInput className="input" placeholder="9" value={s.hours_day||9} onChange={e=>setS({...s,hours_day:parseFloat(e.target.value||9)})}/></F>
        <F label="Hours per week"><NumberInput className="input" placeholder="45" value={s.hours_week||45} onChange={e=>setS({...s,hours_week:parseFloat(e.target.value||45)})}/></F>
        <F label="Break minutes" hint="Unpaid break length"><NumberInput className="input" placeholder="30" value={s.break_minutes||30} onChange={e=>setS({...s,break_minutes:parseInt(e.target.value||30)})}/></F>
        <F label="Archive completed projects after (days)" hint="Complete projects move to Archived automatically"><NumberInput className="input" placeholder="30" value={s.archive_after_days ?? 30} onChange={e=>setS({...s,archive_after_days:parseInt(e.target.value||0)})} data-testid="settings-archive-days"/></F>
        <F label="Report Builder default margin % — Materials / Labour / Subcontractors">
          <div className="flex gap-2">
            {[["default_margin_materials",15],["default_margin_labour",15],["default_margin_subcontractors",10]].map(([k,d]) => (
              <NumberInput key={k} className="input w-20" value={s[k] ?? d} onChange={e=>setS({...s,[k]:parseFloat(e.target.value||0)})} data-testid={`settings-${k}`}/>))}
          </div>
        </F>
        <LogoUpload />
        <CompletionTemplate s={s} setS={setS} />
        <label className="text-sm flex items-center gap-2 col-span-2"><input type="checkbox" checked={!!s.break_default_on} onChange={e=>setS({...s,break_default_on:e.target.checked})}/>Default to break taken</label>
      </div>
      <h3 className="text-silver mt-4 mb-1">Home base — weather defaults</h3>
      <p className="text-xs opacity-70">Weather only shows for projects outside this town. Dashboard & Works Programme pull the home-base forecast from here.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Home base town" hint="e.g. Masterton"><input className="input" placeholder="Masterton" value={s.home_base_town||""} onChange={e=>setS({...s,home_base_town:e.target.value})} data-testid="settings-home-base-town"/></F>
        <F label="Region"><input className="input" placeholder="Wairarapa" value={s.home_base_region||""} onChange={e=>setS({...s,home_base_region:e.target.value})}/></F>
        <F label="Latitude" hint="Decimal (defaults to Masterton -40.9549)"><NumberInput className="input" step="0.0001" placeholder="-40.9549" value={s.home_base_lat ?? ""} onChange={e=>setS({...s,home_base_lat:parseFloat(e.target.value)||null})}/></F>
        <F label="Longitude" hint="Decimal (defaults to Masterton 175.6577)"><NumberInput className="input" step="0.0001" placeholder="175.6577" value={s.home_base_lon ?? ""} onChange={e=>setS({...s,home_base_lon:parseFloat(e.target.value)||null})}/></F>
      </div>
      <button className="btn-silver" onClick={save} data-testid="btn-save-settings">Save settings</button>
    </div>
  );
}

function ImportTool() {
  const [form, setForm] = useState({ old_hub_url: window.location.origin, admin_email: "mock@old", admin_password: "any" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  async function run(dry_run) {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/config/import-old-hub", { ...form, dry_run });
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Failed" });
    } finally { setBusy(false); }
  }
  return (
    <div className="card space-y-3" data-testid="import-tool">
      <p className="text-sm opacity-70">Imports read-only from an existing LW&Co Hub. Credentials are used only for this session. Point this at the preview URL to run against the built-in mock (<code>password = "any"</code>).</p>
      <input className="input" placeholder="Old hub URL" value={form.old_hub_url} onChange={e=>setForm({...form,old_hub_url:e.target.value})}/>
      <input className="input" placeholder="Admin email on old hub" value={form.admin_email} onChange={e=>setForm({...form,admin_email:e.target.value})}/>
      <input className="input" type="password" placeholder="Admin password on old hub" value={form.admin_password} onChange={e=>setForm({...form,admin_password:e.target.value})}/>
      <div className="flex gap-2">
        <button className="btn-silver" disabled={busy} onClick={()=>run(true)} data-testid="btn-import-dryrun">Dry run</button>
        <button className="btn-silver" disabled={busy} onClick={()=>run(false)} data-testid="btn-import-run">Import now</button>
      </div>
      {result && <pre className="bg-neutral-900 p-3 text-xs overflow-auto rounded">{JSON.stringify(result,null,2)}</pre>}
    </div>
  );
}
